import { useEffect, useRef, useState } from 'react';
import { loadScript } from '@paypal/paypal-js';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../App';

// One-time orders and monthly subscriptions need different PayPal SDK intents
// on the same page, so we load two isolated SDK instances via data namespaces
// (the SDK cannot be loaded twice into the default namespace).
export default function PayPalCheckout({ pkg, prospectEmail, onClose }) {
  const [sdkReady, setSdkReady] = useState(false);
  const [failed, setFailed] = useState(false);
  const buttonsRef = useRef(null);
  const msgRef = useRef(null);
  const renderedRef = useRef(false);
  const monthly = pkg.unit === '/month';

  useEffect(() => {
    let cancelled = false;

    (async () => {
      let clientId;
      try {
        const { data } = await api.get('/paypal/config');
        clientId = data.client_id;
      } catch {
        if (!cancelled) {
          toast.error('PayPal is being linked — please use card checkout.');
          onClose();
        }
        return;
      }
      try {
        const paypal = await loadScript({
          clientId,
          currency: 'USD',
          locale: 'en_US',
          components: 'buttons,messages',
          'enable-funding': 'venmo,paylater,card',
          intent: monthly ? 'subscription' : 'capture',
          ...(monthly ? { vault: true } : {}),
          dataNamespace: monthly ? 'gslab_paypal_sub' : 'gslab_paypal_order',
        });
        if (!cancelled && paypal) setSdkReady(true);
      } catch {
        if (!cancelled) setFailed(true);
      }
    })();

    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!sdkReady || renderedRef.current) return;
    const paypal = monthly ? window.gslab_paypal_sub : window.gslab_paypal_order;
    if (!paypal) return;
    renderedRef.current = true;

    const startCheckout = async () => {
      const { data } = await api.post(monthly ? '/paypal/subscription' : '/paypal/order', {
        package_id: pkg.id,
        origin_url: window.location.origin,
        prospect_email: prospectEmail,
        ref: sessionStorage.getItem('gslab_ref') || '',
      });
      return monthly ? data.subscription_id : data.order_id;
    };

    paypal.Buttons({
      style: { layout: 'vertical', shape: 'rect', label: 'paypal', height: 45 },
      ...(monthly ? { createSubscription: startCheckout } : { createOrder: startCheckout }),
      onApprove: async (data) => {
        try {
          if (monthly) {
            await api.post('/paypal/subscription/activate', { subscription_id: data.subscriptionID });
            window.location.href = `/payment/success?subscription_id=${data.subscriptionID}`;
          } else {
            await api.post('/paypal/capture', { order_id: data.orderID });
            window.location.href = `/payment/success?token=${data.orderID}`;
          }
        } catch {
          toast.error('Payment is processing — check your account in a moment.');
          window.location.href = '/payment/success';
        }
      },
      onError: () => toast.error('PayPal checkout hit a snag — please try again or use card checkout.'),
    }).render(buttonsRef.current).catch(() => setFailed(true));

    if (!monthly && msgRef.current && paypal.Messages) {
      try {
        paypal.Messages({ amount: pkg.price, style: { layout: 'text', text: { color: 'black' } } })
          .render(msgRef.current);
      } catch { /* Pay Later messaging is optional */ }
    }
  }, [sdkReady]);

  return (
    <div data-testid="paypal-checkout-panel">
      {!monthly && <div ref={msgRef} className="mb-3 text-slate-800" data-testid="paypal-paylater-message" />}
      {failed ? (
        <p className="text-sm text-slate-600 py-6 text-center">
          PayPal buttons could not load — please try again or use card checkout.
        </p>
      ) : (
        <>
          {!sdkReady && (
            <div className="flex justify-center py-10">
              <Loader2 size={22} className="animate-spin text-slate-400" />
            </div>
          )}
          <div ref={buttonsRef} data-testid="paypal-buttons-stack" />
        </>
      )}
    </div>
  );
}
